import type Store from '@ember-data/store';
import { concat } from '@ember/helper';
import { service } from '@ember/service';
import dayjs from 'dayjs';
import {
  validateDate,
  validatePresence
} from 'ember-changeset-validations/validators';
import type { BufferedChangeset, ValidatorMap } from 'ember-changeset/types';
import t from 'ember-intl/helpers/t';
import type IntlService from 'ember-intl/services/intl';
import and from 'ember-truth-helpers/helpers/and';
import gt from 'ember-truth-helpers/helpers/gt';
import not from 'ember-truth-helpers/helpers/not';
import SectionContent from 'parog-web/components/care-giver/section-content';
import type { ForcBaseArgs } from 'parog-web/components/forc/abstract/base-component';
import CrudJournalEventBaseComponent from 'parog-web/components/forc/abstract/crud-journal-event-based-component';
import Provider from 'parog-web/components/forc/provider';
import JournalEventRetrievalStrategies from 'parog-web/components/forc/utils/journal-event-retrieval-strategies';
import LabeledPatientInformation from 'parog-web/components/labeled-patient-information';
import AccordionBlock from 'parog-web/components/medical-history/accordion-block';
import ResponsibleDropdown from 'parog-web/components/planification/responsible-dropdown';
import TaskDropdownItems from 'parog-web/components/task/task-dropdown-items';
import { dateLongFormat } from 'parog-web/helpers/datepicker-format';
import TaskPlannedJournalEvent from 'parog-web/models/task-planned-journal-event';
import type { SemeiaMedicSession } from 'parog-web/services/session';
import tWithDefault from 'parog-web/utils/t-with-default';
import { computeDateTime } from 'parog-web/utils/time';
import DropdownButton from 'ui-kit/components/dropdown-button';
import { possibleTypes } from './utils/get-task-types';
interface Args extends ForcBaseArgs {
  isDisplayedFromPanel: boolean;
  shouldHideTitle?: boolean;
}

type TaskPlannedEditionFormChangeset = BufferedChangeset & {
  occurredAt: Date;
  occurredAtDate: Date;
  occurredAtTime: Date;
};

export default class TaskPlanificationComponent extends CrudJournalEventBaseComponent<
  TaskPlannedJournalEvent,
  Args
> {
  static override forcDisplayName = 'Tâche';
  static override fullComponentId = 'task/edition-form';
  static override journalEventClass = TaskPlannedJournalEvent;

  @service declare intl: IntlService;
  @service declare session: SemeiaMedicSession;
  @service declare store: Store;

  formatter = {
    date: dateLongFormat
  };
  override retrieveJournalEvent =
    JournalEventRetrievalStrategies.sourceJournalEvent;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  saveOnProcessed = async function saveOnProcessed(this: any): Promise<void> {
    if (!this.isDirty) {
      await this.afterCompleted();
      return;
    }
    await this.onSubmit.perform();
  }.bind(this);

  override get forcDisplayName() {
    return this.shouldHideTitle ? '' : super.forcDisplayName;
  }

  get isAlloLabo() {
    return this.changeset.get('taskType') === 'bio-exams-missing';
  }

  get labCareGivers() {
    return this.patient.careGiverAddedJournalEvents.filter(
      careGiver => careGiver.role == 'analistique-laboratory'
    );
  }

  override get model() {
    // eslint-disable-next-line parog-web/no-use-of-model-in-crud-forc
    const model = this.isNewMode ? this.newModel() : super.model;
    model.occurredAtDate = model.occurredAt;
    model.occurredAtTime = model.occurredAt;
    return model;
  }

  get possibleTypes(): string[] {
    return possibleTypes(this.intl, this.args.patient, this.session.medic);
  }

  get selected() {
    const selectedTaskType = this.changeset.get('taskType');
    // When we load the form and the selected task type is not in the `possibleTypes` array
    // the task will not be translated in the template.
    // This can happen for some rules which plannify task that are not included in the `possibleTypes`
    // or when the medic enters a "custom" task type
    if (selectedTaskType && !this.possibleTypes.includes(selectedTaskType)) {
      return tWithDefault(
        this.intl,
        `task.types.${selectedTaskType}`,
        selectedTaskType
      );
    }
    return selectedTaskType;
  }

  /** @type {boolean} */
  get shouldDisplayProcessButton() {
    return this.isEditMode && !this.isReadOnly;
  }

  get shouldHideTitle() {
    return this.args.shouldHideTitle;
  }

  get sourceJournalEvent2(): TaskPlannedJournalEvent {
    return this.sourceJournalEvent as TaskPlannedJournalEvent;
  }

  override get validations(): ValidatorMap {
    const validations = this.journalEventClass.validations(this.intl);
    validations['occurredAtDate'] = [
      validatePresence({
        presence: true,
        description: 'La date'
      }),
      validateDate({
        message:
          'Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date',
        onOrAfter: new Date('01/01/1900')
      }),
      validateDate({
        message:
          'Cette donnée ne peut être déclarée plus de 50 ans en avance. Veuillez modifier la date',
        onOrBefore: dayjs().add(50, 'years').toDate()
      })
    ];
    validations['occurredAtTime'] = validatePresence({
      presence: true,
      description: "L'heure de la tâche"
    });
    validations['lastHprimDate'] = [
      validateDate({
        allowBlank: true,
        message:
          'Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date',
        onOrAfter: new Date('01/01/1900')
      }),
      validateDate({
        allowBlank: true,
        message:
          'Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date',
        onOrBefore: dayjs().endOf('day').toDate()
      })
    ];
    return validations;
  }

  override computeChangeset(): BufferedChangeset {
    const changeset =
      super.computeChangeset() as TaskPlannedEditionFormChangeset;

    changeset.on('afterValidation', key => {
      if (!['occurredAtDate', 'occurredAtTime'].includes(key)) return;

      const occurredAtDate = changeset.occurredAtDate;
      const occurredAtTime = changeset.occurredAtTime;
      changeset.set(
        'occurredAt',
        computeDateTime(occurredAtDate, occurredAtTime)
      );
    });
    return changeset;
  }

  onChange = () => {
    if (this.isNewMode) {
      if (this.isAlloLabo) {
        this.changeset.set(
          'comments',
          'Fréquence de bio: \nDate de dernière bio: \nFréquence de consultation: \nDate de la prochaine consultation:'
        );
      } else {
        this.changeset.set('comments', '');
      }
    }
  };

  private newModel(): typeof Proxy {
    const store = this.store;
    const modelName = this.journalEventClass.modelName;
    const save = async function (this: typeof Proxy) {
      await store.createRecord(modelName, this).save();
    };
    const handler = {
      get(target, prop, receiver) {
        if (prop == 'save') return save;
        return Reflect.get(target, prop, receiver);
      }
    };

    const attrs = {
      occurredAt: dayjs().hour(12).minute(0).toDate(),
      responsible: this.session.medic
    };
    return new Proxy(attrs, handler);
  }

  <template>
    <Provider @isHeaderHidden={{@isDisplayedFromPanel}} @forcComponent={{this}}>
      <:header>
        {{#if (and this.shouldDisplayProcessButton)}}
          <DropdownButton
            @title='Traiter'
            data-test-edition-form-task-button
            as |dropdown|
          >
            <TaskDropdownItems
              @dropdown={{dropdown}}
              @task={{this.sourceJournalEvent2}}
              @onTaskProcessedOrShifted={{this.saveOnProcessed}}
              @shouldHideShiftOther={{true}}
            />
          </DropdownButton>
        {{/if}}
      </:header>
      <:content as |Form|>
        <div class='field'>
          <Form.Date
            @labelName='Date de la tâche'
            @propertyName='occurredAtDate'
            @formatter={{this.formatter}}
          />
        </div>
        <div class='field'>
          <Form.Time
            @labelName='Horaire de la tâche'
            @propertyName='occurredAtTime'
          />
        </div>

        <div class='field'>
          <ResponsibleDropdown
            @patient={{this.patient}}
            @labelName='Responsable'
            @propertyName='responsible'
            @changeset={{this.changeset}}
            @isReadOnly={{this.isReadOnly}}
          />
        </div>

        <div class='field'>
          <Form.Dropdown
            @labelName='Titre'
            @propertyName='taskType'
            @allowAdditions={{true}}
            @onChangesetUpdate={{this.onChange}}
            as |dropdown|
          >
            {{#each this.possibleTypes as |taskType|}}
              <dropdown.Item
                @key={{taskType}}
                @value={{t (concat 'task.types.' taskType)}}
              />
            {{/each}}
          </Form.Dropdown>
        </div>
        <div class='field'>
          {{#if this.isAlloLabo}}
            <Form.Date
              @labelName='Date du dernier HPRIM reçu'
              @propertyName='lastHprimDate'
              @formatter={{this.formatter}}
            />
            {{#if
              (and (gt this.labCareGivers.length 0) (not @isDisplayedFromPanel))
            }}
              <SectionContent
                @patient={{@patient}}
                @careGivers={{this.labCareGivers}}
              />
            {{/if}}
            <AccordionBlock
              @title='Informations sur le patient'
              @isActive={{true}}
              data-test-patient-information
            >
              <div class='ui two column vertically padded grid'>
                <div class='row !p-0 !pt-0'>
                  <LabeledPatientInformation
                    @patient={{@patient}}
                    @propertyName='birthday'
                    @description='Date de naissance'
                    @format='birthday'
                  />
                  <LabeledPatientInformation
                    @patient={{@patient}}
                    @propertyName='birthName'
                    @description='Nom de naissance'
                    @format='titleize'
                  />
                </div>
                <div class='row !p-0 !pt-2'>
                  <LabeledPatientInformation
                    @patient={{@patient}}
                    @propertyName='address'
                    @description='Adresse'
                  />
                  <LabeledPatientInformation
                    @patient={{@patient}}
                    @propertyName='moreAddressInformation'
                    @description='Informations complémentaires'
                  />
                </div>
                <div class='row !p-0 !pt-2'>
                  <LabeledPatientInformation
                    @patient={{@patient}}
                    @propertyName='city'
                    @description='Ville'
                    @format='titleize'
                  />
                  <LabeledPatientInformation
                    @patient={{@patient}}
                    @propertyName='postcode'
                    @description='Code postal'
                  />
                </div>
                <div class='row !p-0 !pt-2'>
                  <LabeledPatientInformation
                    @patient={{@patient}}
                    @propertyName='socialSecurityNumber'
                    @description='Numéro de sécurité sociale'
                  />
                  <LabeledPatientInformation
                    @patient={{@patient}}
                    @propertyName='phone'
                    @description='Numéro de téléphone'
                    @format='phone'
                  />
                </div>
                <div class='row !p-0 !pt-2'>
                  <LabeledPatientInformation
                    @patient={{@patient}}
                    @propertyName='healthCentre.name'
                    @description='Centre de soin'
                  />
                  <LabeledPatientInformation
                    @patient={{@patient}}
                    @propertyName='referentMedic.fullName'
                    @description='Référent médical'
                    @format='titleize'
                  />
                </div>

              </div>
            </AccordionBlock>
          {{/if}}
        </div>

        <div class='field'>
          <Form.TextArea @propertyName='comments' @rows={{4}} />
        </div>
      </:content>
      <:footer as |Footer|>
        {{#unless @isDisplayedFromPanel}}
          <Footer.CancelButton />
        {{/unless}}
        <Footer.SubmitButton />
      </:footer>
    </Provider>
  </template>
}
